<template>
  <div
    class="post bg-postBackground !rounded-none !px-0"
    :style="{ marginBottom: `${gap}px` }"
  >
    <div class="post__avatar-container">
      <BaseAvatar
        v-if="!isOrg"
        :storage-id="getData.storageId"
        :verified="getData.verified"
        :href="userRoute"
        :isAdmin="getData.isAdmin && isAdmin"
        :rating="getData.rating"
        :isCurrentUser="userdata?.id === getData.userId"
      />
      <BaseAvatar
        v-else
        :storage-id="getData.storageId"
        :verified="getData.verified"
        :isAdmin="getData.isAdmin && isAdmin"
        :rating="getData.rating"
        :isCurrentUser="userdata?.id === getData.userId"
      />
      <ReputationBadge
        v-if="getData.rating"
        :score="getData.rating"
        :showTierName="true"
        :isCurrentUser="userdata?.id === getData.userId"
        :tierSet="tierSet"
        :class="[
          'mx-auto mt-1',
          { 'prominent-badge': userdata?.id === getData.userId },
        ]"
      />
    </div>
    <div class="post__body">
      <div class="post__header">
        <div class="post__user-info">
          <router-link
            v-if="!isOrg"
            :to="userRoute"
            class="post__username hover:underline"
            >{{ fullName }}</router-link
          >
          <router-link
            v-else
            :to="userRoute"
            target="_blank"
            class="post__username hover:underline"
            >{{ fullName }}</router-link
          >
          <span
            v-if="getData.username"
            class="post__username post__username--secondary"
            >@{{ getData.username }}</span
          >
        </div>
        <div class="ml-auto">
          <BaseButton
            v-if="isOrg"
            class="base-btn--small !max-w-[78px] !min-w-[78px] mr-4"
            mode="secondary"
            @click="addUserToOrg"
          >
            Add
          </BaseButton>
          <ButtonFollow
            v-if="isAuth && data.id !== userdata.id"
            :loading="followLoading"
            :isFollow="isFollowing"
            @click="handleFollow(getData.userId)"
            class="suggested-post__follow-button"
            :class="{ 'suggested-post__unfollow-button': isFollowing }"
          />
        </div>
      </div>
      <div class="post__content">
        <span
          v-if="getData.bio"
          class="text-text02 cutLines text-xs text-left ml-5"
          v-html="formatBio(getData.bio)"
        ></span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";

import { data } from "autoprefixer";
import { useToast } from "primevue/usetoast";
import { useStore } from "vuex";

import { authErrorHandler } from "@/services/error/authHandler.js";
import { useFollowNotification } from "@/utils/notifications/notifiationMessages";
import { abbreviateNumber } from "@/utils/utils.js";
import ReputationBadge from "@/components/ReputationBadge.vue";
import { useTierSet } from "@/utils/reputationTiers.js";

import ButtonFollow from "../../components/ui-kit/ButtonFollow/index.vue";
import getFullName from "../../utils/user/getFullName";

const notify = useFollowNotification();
const toast = useToast();
const store = useStore();
const userdata = computed(() => store.getters.getUser);
const isAdmin = computed(
  () => userdata.value?.roles?.includes("admin") || false
);

// Tier set for reputation badges
const { tierSet } = useTierSet();

const emit = defineEmits(["adduser"]);

const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
  type: {
    type: String,
    default: "post",
  },
  isAuth: {
    type: Boolean,
    default: false,
  },
  isOrg: {
    type: Boolean,
    default: false,
  },
  gap: {
    type: String,
    default: "15",
  },
});

const getData = computed(() => {
  let obj = null;

  if (props.type === "post") {
    let post = props.data;
    obj = { ...post };
    obj.storageId = post.avatarFile?.storageId;
    obj.verified = post.verified;
    obj.userId = post.id;
    obj.roles = post.authorUser?.roles.includes("admin") || false;
  } else if (props.type === "user") {
    const user = props.data;
    obj = { ...user };
    obj.storageId = user?.avatarFile?.storageId;
    obj.userId = user.id;
    obj.isAdmin = user.roles.includes("admin") || false;
  }

  return obj;
});

const getMyProfile = async () => {
  await store.dispatch("getMe");
};
const followLoading = ref(false);

const userRoute = computed(() => {
  return !props.data?.username
    ? `/profile/${props.data?.id}`
    : `/user/${props.data?.username}`;
});

const isFollowing = computed(() => {
  if (!Array.isArray(userdata.value?.following)) return null;
  return userdata.value.following.includes(getData.value.userId);
});
const fullName = computed(() => {
  let firstName = getData.value.firstName;
  let lastName = getData.value.lastName;
  let maxWidth = 9;

  if (!firstName && !lastName) {
    return getData.value.username;
  }

  firstName = firstName
    ? firstName.length <= maxWidth
      ? firstName
      : firstName.slice(0, maxWidth) + ".."
    : "";
  lastName = lastName
    ? lastName.length <= maxWidth
      ? lastName
      : lastName.slice(0, maxWidth) + ".."
    : "";

  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  } else {
    return firstName || lastName;
  }
});

const addUserToOrg = () => {
  emit("adduser");
};

const handleFollow = async (id) => {
  followLoading.value = true;
  if (!isFollowing.value) {
    try {
      const res = await fetch(`/api/users/${id}/follow`, {
        method: "POST",
      });

      const data = await res.json();
      if (data.statusCode === 403 && data.message === "User blocked you") {
        toast.add({
          severity: "warn",
          summary: "Why can't you do this?",
          detail:
            "This user has blocked you, so you can't perform this action.",
          life: 7000,
        });
        return;
      }
      if (
        (res.status === 401 || res.status === 403) &&
        !data.message === "User blocked you"
      ) {
        authErrorHandler(res);
        return;
      }
      if (data.statusCode === 409) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: data.message,
          life: 7000,
        });
        return;
      }

      if (data.error) {
        const followerName = getFullName(props.data);
        notify("error", "followed", followerName);
        console.log("error", data.message);
      } else {
        await getMyProfile();
        const followerName = getFullName(props.data);
        notify("success", "followed", followerName);
        userdata.value.following.push(id);
      }
    } catch (error) {
      console.log("e", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error.message,
        life: 7000,
      });
      console.log("error", error);
    } finally {
      followLoading.value = false;
    }
  } else {
    try {
      const res = await fetch(`/api/users/${id}/unfollow`, {
        method: "POST",
      });
      if (res.status === 401 || res.status === 403) {
        authErrorHandler(res);
        return;
      }
      const data = await res.json();
      if (data.statusCode === 409) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: data.message,
          life: 7000,
        });
        return;
      }
      if (data.error) {
        console.log("error", data.message);
      } else {
        await getMyProfile();
        const followerName = getFullName(props.data);
        notify("success", "unfollowed", followerName);
        userdata.value.following = userdata.value.following.filter(
          (userId) => userId !== id
        );
      }
    } catch (error) {
      console.log("error", error);
      store.commit("setUserDataError", error);
      console.log("e", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error.message,
        life: 7000,
      });
      console.log("error", error);
    } finally {
      followLoading.value = false;
    }
  }
};

const formatBio = (bio) => {
  return bio.replace(
    /(https?:\/\/[^\s]+)/g,
    '<a href="$1" target="_blank">$1</a>'
  );
};
</script>

<style lang="scss" scoped>
.post {
  width: 100%;
  display: flex;
  align-items: flex-start;

  &__avatar-container {
    width: 48px;
    flex-shrink: 0;
  }

  &__user-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__username {
    align-self: flex-start;
    font-size: 0.8rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 108px;
    font-weight: 400 !important;

    &--secondary {
      color: var(--text-02);
      font-size: 0.75rem;
    }
  }

  &__body {
    flex-grow: 1;
    overflow-wrap: break-word;
    overflow: hidden;
  }

  &__badge {
    width: 48px;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1px;
    margin-bottom: 4px;
  }

  &__content {
    margin-top: 4px;
  }
}

.suggested-post__follow-button {
  margin-bottom: 0 !important;
  transition: 0.2s ease-in;
  font-size: 0.8rem;

  &:hover {
    background-color: var(--hover-tertiary);
    color: var(--inverse-01);
  }
}

.suggested-post__unfollow-button {
  border: 1px solid var(--interactive-03);
  background-color: transparent;
}
</style>
